import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Table,
  Badge,
  Modal,
  Form,
  Collapse,
} from 'react-bootstrap';
import { FaArrowLeft, FaSearch, FaTrash, FaUtensils, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import consultaServiciosDuplicadosApi from '../../../services/api/consultaServiciosDuplicadosApi';
import { clienteApi } from '../../../services/api/clienteApi';
import 'react-toastify/dist/ReactToastify.css';

const hoyIso = () => new Date().toISOString().split('T')[0];

const hace30DiasIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};

const formatFechaHora = (val) => {
  if (!val) return '—';
  const s = String(val);
  return s.length >= 16 ? s.slice(0, 16).replace('T', ' ') : s;
};

const ConsultaServiciosDuplicadosComensales = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const [loading, setLoading] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [expanded, setExpanded] = useState({});

  const [fechaDesde, setFechaDesde] = useState(hace30DiasIso());
  const [fechaHasta, setFechaHasta] = useState(hoyIso());
  const [idCliente, setIdCliente] = useState('');
  const [documento, setDocumento] = useState('');
  const [minComedores, setMinComedores] = useState('2');
  const [maxComedores, setMaxComedores] = useState('3');

  const [modalOpen, setModalOpen] = useState(false);
  const [atencionPendiente, setAtencionPendiente] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);

  useEffect(() => {
    if (!token || !selectedProject) {
      navigate('/');
    }
  }, [navigate, token, selectedProject]);

  useEffect(() => {
    const cargarClientes = async () => {
      if (!selectedProject?.id) return;
      try {
        const res = await clienteApi.getByProyectoRest(selectedProject.id);
        const lista = res.data?.data || res.data || [];
        setClientes(Array.isArray(lista) ? lista : []);
      } catch {
        setClientes([]);
      }
    };
    cargarClientes();
  }, [selectedProject?.id]);

  const buildParams = () => {
    const params = {
      min_comedores: parseInt(minComedores, 10) || 2,
    };
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (idCliente) params.id_cliente = parseInt(idCliente, 10);
    if (documento.trim()) params.documento = documento.trim();
    if (maxComedores.trim() !== '') {
      params.max_comedores = parseInt(maxComedores, 10);
    }
    return params;
  };

  const buscar = useCallback(async () => {
    setLoading(true);
    setGrupos([]);
    setExpanded({});
    try {
      const res = await consultaServiciosDuplicadosApi.listarCasos(buildParams());
      const body = res.data;
      if (body.success) {
        setGrupos(body.grupos || []);
        if ((body.grupos || []).length === 0) {
          toast.info('No se encontraron casos con los filtros indicados.');
        } else {
          const reg = body.casos_registrados_auditoria;
          toast.success(
            `${body.total_grupos} caso(s) encontrado(s)` +
              (reg != null ? ` · ${reg} en auditoría` : '')
          );
        }
      } else {
        toast.error(body.message || 'No se pudo completar la consulta.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Error al consultar.';
      toast.error(typeof msg === 'string' ? msg : 'Error al consultar.');
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta, idCliente, documento, minComedores, maxComedores]);

  const toggleExpand = (clave) => {
    setExpanded((prev) => ({ ...prev, [clave]: !prev[clave] }));
  };

  const solicitarEliminar = (grupo, atencion) => {
    setAtencionPendiente({ grupo, atencion });
    setModalOpen(true);
  };

  const ejecutarEliminar = async () => {
    if (!atencionPendiente) return;
    const { atencion } = atencionPendiente;
    setEliminandoId(atencion.id_turno_detalle);
    setModalOpen(false);
    try {
      const res = await consultaServiciosDuplicadosApi.eliminarAtencion(
        atencion.id_turno_detalle
      );
      if (res.data?.success) {
        toast.success(
          res.data.message ||
            `Eliminado. Cabecera: ${res.data.cabecera_cantidad_antes} → ${res.data.cabecera_cantidad_despues} comensales.`
        );
        await buscar();
      } else {
        toast.error(res.data?.message || 'No se pudo eliminar.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Error al eliminar.';
      toast.error(typeof msg === 'string' ? msg : 'Error al eliminar.');
    } finally {
      setEliminandoId(null);
      setAtencionPendiente(null);
    }
  };

  const estilosDh = `
    .dh-reporte-bg {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      min-height: 100vh;
    }
    .dh-main-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 2rem 0;
      margin-bottom: 2rem;
      border-radius: 0 0 30px 30px;
      box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
    }
    .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
    .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
    .dh-card {
      border: none;
      border-radius: 20px;
      box-shadow: 0 15px 35px rgba(0,0,0,0.1);
      background: white;
    }
    .dh-card-header {
      border-radius: 20px 20px 0 0;
      padding: 1.25rem 1.75rem;
      border: none;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      font-weight: 700;
    }
    .btn-dh-primary {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      border: none;
      color: white;
      border-radius: 12px;
      padding: 10px 18px;
      font-weight: 600;
    }
    .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
    .btn-dh-secondary {
      background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
      border: none;
      color: white;
      border-radius: 12px;
      padding: 10px 18px;
      font-weight: 600;
    }
    .btn-dh-secondary:hover { color: white; }
    .btn-dh-danger {
      background: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
      border: none;
      color: white;
      border-radius: 10px;
      font-weight: 600;
    }
    .btn-dh-danger:hover { color: white; filter: brightness(1.05); }
    .dh-form-label { font-weight: 600; color: #374151; }
    .dh-form-control { border-radius: 10px; }
    .dh-alert-info {
      background: #eef2ff;
      border: 1px solid #c7d2fe;
      border-radius: 12px;
      color: #3730a3;
    }
    .grupo-row { cursor: pointer; }
    .grupo-row:hover { background-color: #f8f9ff !important; }
  `;

  return (
    <>
      <style>{estilosDh}</style>
      <div className="dh-reporte-bg">
        <ToastContainer position="top-right" />
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <h1 className="dh-main-title">
                  <FaUtensils className="me-2" />
                  Servicios duplicados en comedores
                </h1>
                <p className="dh-main-subtitle">
                  Comensales que almorzaron (o tomaron el mismo servicio) en dos o más comedores
                  el mismo día. Identificado por proyecto y cliente. Puede eliminar el registro
                  incorrecto; la cabecera del turno se recalcula automáticamente.
                </p>
              </Col>
              <Col xs="auto">
                <Button className="btn-dh-secondary" onClick={() => navigate(-1)}>
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">Filtros</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Label className="dh-form-label">Fecha desde</Form.Label>
                  <Form.Control
                    type="date"
                    className="dh-form-control"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Fecha hasta</Form.Label>
                  <Form.Control
                    type="date"
                    className="dh-form-control"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Cliente</Form.Label>
                  <Form.Select
                    className="dh-form-control"
                    value={idCliente}
                    onChange={(e) => setIdCliente(e.target.value)}
                  >
                    <option value="">Todos los clientes</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Documento</Form.Label>
                  <Form.Control
                    className="dh-form-control"
                    placeholder="Coincidencia parcial"
                    value={documento}
                    onChange={(e) => setDocumento(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Mín. comedores</Form.Label>
                  <Form.Select
                    className="dh-form-control"
                    value={minComedores}
                    onChange={(e) => setMinComedores(e.target.value)}
                  >
                    <option value="2">2 o más</option>
                    <option value="3">3 o más</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Máx. comedores</Form.Label>
                  <Form.Select
                    className="dh-form-control"
                    value={maxComedores}
                    onChange={(e) => setMaxComedores(e.target.value)}
                  >
                    <option value="">Sin límite</option>
                    <option value="2">Solo 2</option>
                    <option value="3">Hasta 3</option>
                  </Form.Select>
                </Col>
              </Row>
              <div className="mt-3">
                <Button className="btn-dh-primary" onClick={buscar} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Buscando…
                    </>
                  ) : (
                    <>
                      <FaSearch className="me-2" />
                      Buscar casos
                    </>
                  )}
                </Button>
              </div>
              <Alert variant="info" className="dh-alert-info mt-3 mb-0 small">
                Proyecto activo: <strong>{selectedProject?.nombre || selectedProject?.id}</strong>.
                Cada búsqueda registra los casos en auditoría (tabla{' '}
                <code>servicios_duplicados_comensales</code>) para el reporte en Reportes →
                Alimentación. Expanda la fila para eliminar el registro duplicado.
              </Alert>
            </Card.Body>
          </Card>

          <Card className="dh-card">
            <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
              <span>Resultados</span>
              {grupos.length > 0 && (
                <Badge bg="light" text="dark">
                  {grupos.length} caso(s)
                </Badge>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {grupos.length === 0 ? (
                <p className="p-4 mb-0 text-muted">
                  {loading
                    ? 'Consultando…'
                    : 'Use los filtros y pulse «Buscar casos» para listar duplicados.'}
                </p>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0 align-middle small">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '36px' }} />
                        <th>Cliente</th>
                        <th>Comensal</th>
                        <th>Documento</th>
                        <th>Fecha</th>
                        <th>Servicio</th>
                        <th className="text-center">Comedores</th>
                        <th>Comedores (detalle)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupos.map((grupo) => {
                        const abierto = expanded[grupo.clave_grupo];
                        return (
                          <React.Fragment key={grupo.clave_grupo}>
                            <tr
                              className="grupo-row"
                              onClick={() => toggleExpand(grupo.clave_grupo)}
                            >
                              <td className="text-center text-muted">
                                {abierto ? <FaChevronUp /> : <FaChevronDown />}
                              </td>
                              <td>
                                <div className="fw-semibold">{grupo.nombre_cliente || '—'}</div>
                                {grupo.id_cliente != null && (
                                  <span className="text-muted">ID {grupo.id_cliente}</span>
                                )}
                              </td>
                              <td>{grupo.nombre_comensal}</td>
                              <td>{grupo.numero_documento_identidad || '—'}</td>
                              <td>{grupo.fecha_servicio}</td>
                              <td>{grupo.tipo_servicio}</td>
                              <td className="text-center">
                                <Badge bg="warning" text="dark">
                                  {grupo.cantidad_comedores}
                                </Badge>
                              </td>
                              <td className="text-break">{grupo.comedores_resumen}</td>
                            </tr>
                            <tr>
                              <td colSpan={8} className="p-0 border-0">
                                <Collapse in={abierto}>
                                  <div className="p-3 bg-light border-bottom">
                                    <Table size="sm" bordered className="mb-0 bg-white">
                                      <thead>
                                        <tr>
                                          <th>Comedor</th>
                                          <th>Hora registro</th>
                                          <th className="text-end">Precio</th>
                                          <th className="text-center">Cabecera (comensales)</th>
                                          <th className="text-center" style={{ width: '120px' }}>
                                            Acción
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {grupo.atenciones.map((a) => (
                                          <tr key={a.id_turno_detalle}>
                                            <td>{a.comedor_nombre}</td>
                                            <td>{formatFechaHora(a.fecha_registro)}</td>
                                            <td className="text-end">
                                              {Number(a.precio_venta).toFixed(2)}
                                            </td>
                                            <td className="text-center">
                                              {a.cabecera_cantidad_comensales}
                                              {a.turno_cerrado && (
                                                <Badge bg="secondary" className="ms-1">
                                                  Cerrado
                                                </Badge>
                                              )}
                                            </td>
                                            <td className="text-center">
                                              <Button
                                                size="sm"
                                                className="btn-dh-danger"
                                                disabled={eliminandoId === a.id_turno_detalle}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  solicitarEliminar(grupo, a);
                                                }}
                                              >
                                                {eliminandoId === a.id_turno_detalle ? (
                                                  <Spinner animation="border" size="sm" />
                                                ) : (
                                                  <>
                                                    <FaTrash className="me-1" />
                                                    Eliminar
                                                  </>
                                                )}
                                              </Button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </Collapse>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>

        <Modal show={modalOpen} onHide={() => setModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar registro duplicado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {atencionPendiente && (
              <>
                <p>
                  ¿Eliminar la atención de{' '}
                  <strong>{atencionPendiente.grupo.nombre_comensal}</strong> en{' '}
                  <strong>{atencionPendiente.atencion.comedor_nombre}</strong> (
                  {atencionPendiente.grupo.tipo_servicio},{' '}
                  {atencionPendiente.grupo.fecha_servicio})?
                </p>
                <p className="mb-0 small text-muted">
                  Se actualizará la cantidad de comensales en la cabecera del turno (
                  turno #{atencionPendiente.atencion.id_turno_cabecera}).
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button className="btn-dh-danger" onClick={ejecutarEliminar}>
              Confirmar eliminación
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default ConsultaServiciosDuplicadosComensales;
